// Utility-Funktionen für Adress-Aufspaltung und -Verarbeitung

/**
 * Intelligente Aufspaltung einer Adresse in Straße und Hausnummer
 * Unterstützt verschiedene deutsche Adressformate
 */
export const splitAddress = (address: string): { street: string; houseNumber: string } => {
  if (!address || typeof address !== 'string') {
    return { street: '', houseNumber: '' };
  }

  const trimmed = address.trim();
  
  // Regex-Patterns für verschiedene deutsche Adressformate
  const patterns = [
    // Format: "Musterstraße 123a" oder "Musterstraße 123-125"
    /^(.+?)\s+(\d+(?:[a-zA-Z]|[-\/]\d+[a-zA-Z]?)?)$/,
    // Format: "Musterstraße 123 / 5" (mit Zusätzen)
    /^(.+?)\s+(\d+\s*[\/\\]\s*\d+[a-zA-Z]?)$/,
    // Format: "Musterstraße Nr. 123" oder "Musterstraße No. 123"
    /^(.+?)\s+(?:[Nn]r\.?\s*|[Nn]o\.?\s*)(\d+[a-zA-Z]?)$/,
    // Format: "Am Marktplatz 5-7"
    /^(.+?)\s+(\d+\s*-\s*\d+[a-zA-Z]?)$/,
    // Format: "Hauptstraße 12 A" (mit Leerzeichen vor Buchstabe)
    /^(.+?)\s+(\d+\s+[a-zA-Z])$/,
  ];

  // Versuche jedes Pattern
  for (const pattern of patterns) {
    const match = trimmed.match(pattern);
    if (match) {
      return {
        street: match[1].trim(),
        houseNumber: match[2].trim()
      };
    }
  }

  // Fallback: Suche das letzte Wort, das mit einer Zahl beginnt
  const words = trimmed.split(/\s+/);
  for (let i = words.length - 1; i >= 0; i--) {
    if (/^\d/.test(words[i])) {
      return {
        street: words.slice(0, i).join(' '),
        houseNumber: words.slice(i).join(' ')
      };
    }
  }

  // Wenn kein numerischer Teil gefunden wird, ist alles Straße
  return {
    street: trimmed,
    houseNumber: ''
  };
};

/**
 * Kombiniert Straße und Hausnummer zu einer vollständigen Adresse
 */
export const combineAddress = (street: string, houseNumber: string): string => {
  const trimmedStreet = street?.trim() || '';
  const trimmedHouseNumber = houseNumber?.trim() || '';
  
  if (!trimmedStreet && !trimmedHouseNumber) {
    return '';
  }
  
  if (!trimmedStreet) {
    return trimmedHouseNumber;
  }
  
  if (!trimmedHouseNumber) {
    return trimmedStreet;
  }
  
  return `${trimmedStreet} ${trimmedHouseNumber}`;
};

/**
 * Formatiert eine Adresse für die Anzeige
 */
export const formatAddress = (street?: string, houseNumber?: string, zip?: string, city?: string, country?: string): string => {
  const addressLine = combineAddress(street || '', houseNumber || '');
  const locationLine = [zip, city].filter(Boolean).join(' ');
  
  return [addressLine, locationLine, country]
    .filter(Boolean)
    .join(', ');
};

/**
 * Validiert eine deutsche Postleitzahl
 */
export const isValidGermanZip = (zip: string): boolean => {
  return /^\d{5}$/.test(zip?.trim() || '');
};

/**
 * Test-Fälle für die Adress-Aufspaltung
 * Diese Funktion kann für Debug-Zwecke verwendet werden
 */
export const testAddressSplitting = () => {
  const testCases = [
    'Musterstraße 123',
    'Hauptstraße 45a',
    'Am Marktplatz 5-7',
    'Bergstraße 12 A',
    'Lindenallee 23/25',
    'Gartenweg Nr. 8',
    'Schulstraße 1-3',
    'Dorfplatz 99c',
    'Mühlenweg 4 / 6',
    'Kirchenstraße No. 15',
    'Unter den Linden 42',
    'Straße ohne Nummer',
    '123 Nur Nummer',
    '',
  ];

  console.log('🏠 Address Splitting Test Results:');
  testCases.forEach(address => {
    const result = splitAddress(address);
    console.log(`"${address}" → Straße: "${result.street}", Hausnummer: "${result.houseNumber}"`);
  });
}; 